import { useFetcher } from "~/hooks/useFetcher";
import {
  SearchIndexRun,
  SearchIndexRunsResponse,
  SearchIndexStatus,
  SearchIndexTypeResult,
  SearchOrphanResponse,
  searchIndexOrphansPath,
  searchIndexPath,
  searchIndexRunsPath,
} from "~/services/search";
import { Column, EntityTypeTable } from "./EntityTypeTable";
import { IndexContents } from "./IndexContents";
import { IndexHealth } from "./IndexHealth";
import { Section } from "./Section";
import { errorMessage } from "./constants";

const RUN_COLUMNS: Column<SearchIndexTypeResult>[] = [
  { label: "Read", cell: (result) => result.read },
  {
    label: "Embedded",
    hint: "Entities sent to the embedding model. This is what the run cost; the Unchanged ones were free.",
    cell: (result) => result.embedded,
  },
  {
    label: "Rows written",
    hint: "One row per chunk of an embedded entity, so a long entity writes several.",
    cell: (result) => result.chunks,
  },
  { label: "Unchanged", cell: (result) => result.unchanged },
  { label: "No content", cell: (result) => result.blank },
];

/** A run that died mid-way never sets either, so it reads as still running. */
const runTitle = (run: SearchIndexRun) => {
  if (run.completed_at) {
    return `Last run · started ${new Date(run.started_at).toLocaleString()}`;
  }
  return run.error ? "Last run failed" : "Running…";
};

/** What the index holds, and what the newest indexing run did. */
export const IndexTab = () => {
  const status = useFetcher<SearchIndexStatus>(searchIndexPath());
  const orphans = useFetcher<SearchOrphanResponse>(searchIndexOrphansPath());
  const runs = useFetcher<SearchIndexRunsResponse>(searchIndexRunsPath(1));
  const [lastRun] = runs.data?.runs ?? [];

  const refresh = () => Promise.all([status.mutate(), orphans.mutate()]);

  return (
    <div className="flex flex-col gap-6">
      <IndexHealth status={status} orphans={orphans} />

      <IndexContents status={status} orphans={orphans} onPruned={refresh} />

      {runs.error && (
        <p className="text-sm text-destructive">
          Could not read the index runs: {errorMessage(runs.error)}
        </p>
      )}
      {lastRun && (
        <Section title={runTitle(lastRun)} question="What the newest indexing run did, per type.">
          {lastRun.error && (
            <p className="text-sm text-destructive">Indexing failed: {lastRun.error}</p>
          )}
          <EntityTypeTable label="Last run" rows={lastRun.results} columns={RUN_COLUMNS} />
        </Section>
      )}
    </div>
  );
};
